import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import billingInputComponentsMapping from './billing-input-component-mapping';
import billingInputTypes from './billing-input-types';
import { getValueByInputType } from './billing-input-service';
import { v4 as uuid } from 'uuid';

import './billing-input-component.scss';
import { getDateStringToInput, getDateStringToLabel } from '../../../services/date-util-service';
import { getBoardOptionKey } from '../../../hooks/use-board-select/use-board-select-options-service';

export default function BillingInputComponent({
  withTitle = false,
  previewMode = false,
  id,
  type,
  value,
  label = undefined,
  title,
  inputProps,
  onChange,
  boardSelectOptions,
  placeholder = '',
  disabled,
  clearable,
}) {
  const handleInputChange = useCallback(
    (value) => {
      const data = getValueByInputType(type, value);
      onChange(id, { dataSource: type, data });
    },
    [id, type, onChange]
  );

  const getBoardSelectOption = useCallback(
    (value) => {
      for (let group of boardSelectOptions) {
        for (let option of group.options) {
          const currValue = getBoardOptionKey(group.boardId, option.id, group.itemId);
          if (currValue === value) {
            return option;
          }
        }
      }
    },
    [boardSelectOptions]
  );

  const viewPreparedProps = useMemo(() => {
    let tempPreparedProps = { label: label || value };

    if (type === billingInputTypes.date) {
      tempPreparedProps.label = getDateStringToLabel(value);
    }

    if (type === billingInputTypes.boardSelect) {
      tempPreparedProps.label = getBoardSelectOption(value)?.label;
    }

    return tempPreparedProps;
  }, [value, label, type, getBoardSelectOption]);

  const renderViewMode = () => {
    const { label } = viewPreparedProps;
    return <div className="view">{label}</div>;
  };

  const getOptionValue = (options, value) => {
    return options.find((option) => option.value === value);
  };

  const boardItemRenderer = useCallback((kind, { icon, label, iconSize, title }) => {
    return (
      <div className={classnames('board-item-renderer', kind)}>
        <div className="icon-wrapper">{icon({ size: iconSize })}</div>
        <div className="label-wrapper">
          {label}
          <div className="type-wrapper">({title})</div>
        </div>
      </div>
    );
  }, []);

  const preparedProps = useMemo(() => {
    let tempPreparedProps = {};

    if (type === billingInputTypes.boardSelect) {
      tempPreparedProps = {
        options: boardSelectOptions,
        value: getBoardSelectOption(value),
        OptionRenderer: (data) => boardItemRenderer('option', data),
        ValueRenderer: (data) => boardItemRenderer('value', data),
      };
    }

    if (type === billingInputTypes.select) {
      tempPreparedProps = { value: getOptionValue(inputProps.options, value), clearable };
    }

    if (type === billingInputTypes.date) {
      tempPreparedProps = { value: getDateStringToInput(value) };
    }

    return tempPreparedProps;
  }, [type, boardSelectOptions, value, inputProps, boardItemRenderer, getBoardSelectOption, clearable]);

  const renderEditMode = () => {
    const { component: InputComponent, props = {} } = billingInputComponentsMapping[type];

    const mergedProps = {
      id: uuid(),
      size: 's',
      placeholder,
      value,
      disabled,
      ...props,
      ...inputProps,
      ...preparedProps,
      onChange: handleInputChange,
    };

    return (
      <div className="edit">
        {withTitle && <span className="title">{title}</span>}
        <InputComponent {...mergedProps} />
      </div>
    );
  };

  return <div className="billing-input-component">{previewMode ? renderViewMode() : renderEditMode()}</div>;
}
